/**
 * WordPress Dependencies
 */
import React, { useState } from 'react';
import { createHigherOrderComponent } from '@wordpress/compose';
import { addFilter } from '@wordpress/hooks';
import { Fragment } from '@wordpress/element';
import { InspectorControls } from '@wordpress/block-editor';
import { PanelBody, Button, CheckboxControl } from '@wordpress/components';
import { DuplicateIcon, SparklesIcon } from '@heroicons/react/solid';
import { __ } from '@wordpress/i18n';
import { useSelect, dispatch } from '@wordpress/data';
import { getProjectFeature } from '@appmaker/core/store/project';

// Custom event to trigger attribute duplication
const DUPLICATE_WITH_ATTRIBUTES_EVENT = 'appmaker-duplicate-with-attributes';

const CustomDuplicateSection = ({ attributes, clientId, blockName }) => {
	const [duplicateWithAttributes, setDuplicateWithAttributes] = useState(true);
	const [isDisabled, setIsDisabled] = useState(false);

	// Get the complete block data including inner blocks
	const blockData = useSelect(
		(select) => {
			return select('core/block-editor').getBlock(clientId);
		},
		[clientId]
	);

	const handleDuplicate = () => {
		setIsDisabled(true);

		try {
			if (duplicateWithAttributes) {
				// Dispatch custom event with complete original block data
				window.dispatchEvent(
					new CustomEvent(DUPLICATE_WITH_ATTRIBUTES_EVENT, {
						detail: {
							originalClientId: clientId,
							originalAttributes: attributes,
							blockName: blockName,
							originalBlock: blockData, // Include full block data with inner blocks
						},
					})
				);
			} else {
			}

			// Duplicate the block using WordPress data store
			dispatch('core/block-editor').duplicateBlocks([clientId]);
		} catch (error) {
			console.error('Error during duplication:', error);
		}

		// Re-enable button after a short delay
		setTimeout(() => setIsDisabled(false), 1000);
	};
	return (
		<PanelBody 
			title={
				<div className="flex items-center gap-2">
					<SparklesIcon className="w-4 h-4" />
					<span>Block Duplication (Beta)</span>
				</div>
			} 
			initialOpen={true}>
			<div className="space-y-3">
				<CheckboxControl
					label={__('Duplicate with attributes', 'appmaker')}
					help={__(
						'When enabled, the duplicated block will inherit all attributes from the original block.',
						'appmaker'
					)}
					checked={duplicateWithAttributes}
					onChange={setDuplicateWithAttributes}
				/>

				<Button
					variant="secondary"
					onClick={handleDuplicate}
					disabled={isDisabled}
					className="w-full justify-center"
					icon={<DuplicateIcon className="w-4 h-4" />}
				>
					{isDisabled
						? __('Duplicating...', 'appmaker')
						: __('Duplicate Block', 'appmaker')}
				</Button>

				{duplicateWithAttributes && (
					<p className="text-sm text-gray-600">
						{__(
							'This will copy all styling, content, and settings to the new block.',
							'appmaker'
						)}
					</p>
				)}
			</div>
		</PanelBody>
	);
};

const withCustomDuplicateSection = createHigherOrderComponent((BlockEdit) => {
	return (props) => {
		const { attributes, clientId, name: blockName, isSelected } = props;

		// Get project features to check if block duplication is enabled
		const feature = getProjectFeature();
		const isDuplicationEnabled = feature?.enableBlockDuplication;

		return (
			<Fragment>
				<BlockEdit {...props} />
				{isSelected && isDuplicationEnabled && (
					<InspectorControls>
						<CustomDuplicateSection
							attributes={attributes}
							clientId={clientId}
							blockName={blockName}
						/>
					</InspectorControls>
				)}
			</Fragment>
		);
	};
}, 'withCustomDuplicateSection');

// Export the event name for use in the duplication logger
export { DUPLICATE_WITH_ATTRIBUTES_EVENT };

addFilter(
	'editor.BlockEdit',
	'appmaker/custom-duplicate-section',
	withCustomDuplicateSection
);

// Hide default WordPress duplicate functionality via CSS
const hideDefaultDuplicateCSS = `
.block-editor-block-settings-menu__popover .components-menu-item__button[aria-label*="Duplicate"] {
    display: none !important;
}
.block-editor-block-settings-menu__popover .components-menu-group .components-menu-item:has(.components-menu-item__button[aria-label*="Duplicate"]) {
    display: none !important;
}
.components-dropdown-menu__menu .components-menu-item:has([aria-label*="Duplicate"]) {
    display: none !important;
}
`;

// Inject CSS to hide default duplicate
if (typeof document !== 'undefined') {
	const styleElement = document.createElement('style');
	styleElement.textContent = hideDefaultDuplicateCSS;
	document.head.appendChild(styleElement);
}
